import { reactive } from 'vue'
import { CAMERA_TYPES, DroneSubEnumValue, PAYLOAD_TRY_TYPE, PAYLOAD_WIDE_IR, PayloadSubEnumValue2 } from '../types/device'
import * as Cesium from 'cesium'
import {
  BooleanEnum,
  BtnLoading,
  ExecuteRCLostActionType,
  FOCUS_MODE_TYPE,
  FollowBadType,
  FollowType,
  GIMBAL_HEADERING_TYPE,
  GIMBAL_PITCH_MODE_TYPE,
  GIMBAL_ROTATE_MODE_TYPE,
  METERING_MODE_TYPE,
  PointEnum,
  RETURN_MODE_TYPE,
  SCANNING_MODE_TYPE,
  SEQUENCE,
  SHOOT_TYPE,
  WaylineActionType,
  WaylineSmoothType,
  WaylineTypeMap
} from '../types/wayline'
import { getPointPosition } from './kmlParse'
import { GetNewPoint, GetPoint } from './kml'
import { CoordinateMode, TemplateType } from './kmlUtil'
import { calculateCesiumPolylineLength } from './coordinate'

interface GeographicPoint {
  lng: number
  lat: number
  alt?: number
}

interface WaypointConfig {
  payloadEnumValue: PAYLOAD_TRY_TYPE
  distance: number
  duration: number
  autoFlightSpeed: number
  shootType: SHOOT_TYPE
  imageFormat: string[]
  payloadVal: PAYLOAD_TRY_TYPE
}

interface RouteData {
  acquisitionType: string
  imageFormat: string[]
  shootType: string
  autoFlightSpeed: number
  heightMode: string
  globalHeight: number
  GSD: number
  irGSD: number
  orthoLidarOverlapW: number
  margin: number
  finishAction: string
  flyToWaylineMode: string
  takeOffSecurityHeight: number
  distance: number
  duration: number
  area: number
  mapping3dRouteIndex: number
}

//  使用类型化的响应式数据
export const currentPlanarRouteSettingData = reactive<RouteData>({
  acquisitionType: 'mapping2d',
  imageFormat: ['wide'],
  shootType: 'time',
  autoFlightSpeed: 8,
  heightMode: 'EGM96',
  globalHeight: 100,
  GSD: 5,
  irGSD: 5,
  orthoLidarOverlapW: 70,
  margin: 0,
  finishAction: 'goHome',
  flyToWaylineMode: 'safely',
  takeOffSecurityHeight: 20,
  distance: 0,
  duration: 0,
  area: 0,
  mapping3dRouteIndex: 1
})

export function updateCurrentRouteData (data: any): void {
  const folder = data.Document.Folder[0]
  const placemark = folder.Placemark[0]
  const missionConfig = data.Document.missionConfig

  currentPlanarRouteSettingData.acquisitionType = folder.templateType
  currentPlanarRouteSettingData.imageFormat = folder.payloadParam.imageFormat.split(',')
  currentPlanarRouteSettingData.shootType = placemark.shootType
  currentPlanarRouteSettingData.autoFlightSpeed = folder.autoFlightSpeed * 1
  currentPlanarRouteSettingData.heightMode = folder.waylineCoordinateSysParam.heightMode
  currentPlanarRouteSettingData.globalHeight = folder.waylineCoordinateSysParam.globalShootHeight * 1
  currentPlanarRouteSettingData.orthoLidarOverlapW = placemark.overlap.inclinedCameraOverlapH * 1
  currentPlanarRouteSettingData.margin = placemark.margin * 1
  currentPlanarRouteSettingData.finishAction = missionConfig.finishAction
  currentPlanarRouteSettingData.flyToWaylineMode = missionConfig.flyToWaylineMode
}

export function calculateFocalLengthForPayloads (
  focalLength: number,
  payload: keyof typeof PAYLOAD_WIDE_IR,
  cameraType: CAMERA_TYPES
) {
  const payloadConfig = PAYLOAD_WIDE_IR[payload]
  if (!payloadConfig) return 0
  let sensorSize: number
  let baseFocalLength: number
  if (cameraType === CAMERA_TYPES.Ir) {
    sensorSize = payloadConfig.irS * 1e-4
    baseFocalLength = payloadConfig.irF * 0.1
  } else {
    sensorSize = payloadConfig.wideS * 1e-4
    baseFocalLength = payloadConfig.wideF * 0.1
  }
  const h = focalLength * baseFocalLength / (sensorSize * 100)
  return Number(h.toFixed(1))
}

export function calculateFocalLengthForPayload (
  focalLength: number,
  payload: keyof typeof PAYLOAD_WIDE_IR,
  cameraType: CAMERA_TYPES
): number {
  const payloadConfig = PAYLOAD_WIDE_IR[payload]

  if (!payloadConfig) return 0

  const { irS, irF, wideS, wideF } = payloadConfig as any
  let sensorSize: number
  let baseFocalLength: number

  if (cameraType === CAMERA_TYPES.Ir) {
    sensorSize = irS * 1e-4
    baseFocalLength = irF * 0.1
  } else {
    sensorSize = wideS * 1e-4
    baseFocalLength = wideF * 0.1
  }

  if (sensorSize && baseFocalLength) {
    const result = (sensorSize * focalLength * 100) / baseFocalLength
    return Number(result.toFixed(1))
  }

  return 0
}

function calculateAveragePosition (points: number[][]): GeographicPoint {
  let totalLng = 0
  let totalLat = 0

  points.forEach(point => {
    totalLng += Number(point[0])
    totalLat += Number(point[1])
  })

  return {
    lng: totalLng / points.length,
    lat: totalLat / points.length
  }
}

function createGimbalRotateAction (
  pitchAngle: number,
  yawAngle: number = 0
): any {
  return {
    wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ROTATE,
    wpml_actionActuatorFuncParam: {
      wpml_gimbalHeadingYawBase: GIMBAL_HEADERING_TYPE.AIRCRAFT,
      wpml_gimbalRotateMode: GIMBAL_ROTATE_MODE_TYPE.ABSOLUTE_ANGLE,
      wpml_gimbalPitchRotateEnable: 1,
      wpml_gimbalPitchRotateAngle: pitchAngle,
      wpml_gimbalRollRotateEnable: 0,
      wpml_gimbalRollRotateAngle: 0,
      wpml_gimbalYawRotateEnable: 1,
      wpml_gimbalYawRotateAngle: yawAngle,
      wpml_gimbalRotateTimeEnable: 0,
      wpml_gimbalRotateTime: 10,
      wpml_payloadPositionIndex: 0
    }
  }
}
export function createSaveKmlPolygon (o, l) {
  const t = new Date().getTime()
  const m = o.polygon.map(el => [el[0], el[1], el[2]]).join('\n')

  const e = {
    wpml_flyToWaylineMode: o.flyToWaylineMode,
    wpml_finishAction: o.finishAction,
    wpml_exitOnRCLost: ExecuteRCLostActionType.GO_CONTINUE,
    wpml_executeRCLostAction: BtnLoading.GO_BACK,
    wpml_takeOffSecurityHeight: o.takeOffSecurityHeight,
    wpml_takeOffRefPoint: new GetNewPoint(o.startLng, o.startLat, o.startHeight),
    wpml_takeOffRefPointAGLHeight: 0,
    wpml_globalTransitionalSpeed: 15,
    wpml_globalRTHHeight: 100,
    wpml_droneInfo: {
      wpml_droneEnumValue: o.droneEnumValue,
      wpml_droneSubEnumValue: DroneSubEnumValue[o.payloadEnumValue]
    },
    wpml_waylineAvoidLimitAreaMode: 0,
    wpml_payloadInfo: {
      wpml_payloadEnumValue: o.payloadEnumValue,
      wpml_payloadSubEnumValue: PayloadSubEnumValue2[o.payloadEnumValue],
      wpml_payloadPositionIndex: 0
    }
  }
  let a = {
    wpml_elevationOptimizeEnable: 0,
    wpml_smartObliqueEnable: 0,
    wpml_quickOrthoMappingEnable: 0,
    wpml_facadeWaylineEnable: 0,
    wpml_isLookAtSceneSet: 0
  }
  o.acquisitionType === TemplateType.MAPPING_3D && (a = {
    wpml_inclinedGimbalPitch: -45,
    wpml_inclinedFlightSpeed: o.autoFlightSpeed
  })
  const n = {
    wpml_author: o.author,
    wpml_createTime: o.createTime,
    wpml_updateTime: t,
    wpml_missionConfig: {
      ...e
    },
    Folder: [{
      wpml_templateType: o.acquisitionType,
      wpml_templateId: 0,
      wpml_waylineCoordinateSysParam: {
        wpml_coordinateMode: CoordinateMode.WGS84,
        wpml_heightMode: o.heightMode,
        wpml_globalShootHeight: o.globalHeight
      },
      wpml_autoFlightSpeed: o.autoFlightSpeed,
      Placemark: [{
        wpml_caliFlightEnable: 0,
        ...a,
        wpml_shootType: o.shootType,
        wpml_distance: o.distance,
        wpml_duration: o.duration,
        wpml_area: o.area,
        wpml_margin: o.margin,
        wpml_efficiencyFlightModeEnable: 0,
        wpml_overlap: {
          wpml_orthoCameraOverlapH: o.orthoLidarOverlapW,
          wpml_orthoCameraOverlapW: o.orthoLidarOverlapW,
          wpml_inclinedCameraOverlapH: o.orthoLidarOverlapW,
          wpml_inclinedCameraOverlapW: o.orthoLidarOverlapW
        },
        Polygon: {
          outerBoundaryIs: {
            LinearRing: {
              coordinates: m
            }
          }
        },
        wpml_ellipsoidHeight: o.globalHeight,
        wpml_height: o.globalHeight
      }],
      wpml_payloadParam: {
        wpml_payloadPositionIndex: 0,
        wpml_focusMode: FOCUS_MODE_TYPE.FIRST_POINT,
        wpml_meteringMode: METERING_MODE_TYPE.AVERAGE,
        wpml_returnMode: RETURN_MODE_TYPE.SINGLE_RETURN_FIRST,
        wpml_samplingRate: 24e4,
        wpml_scanningMode: SCANNING_MODE_TYPE.REPETITIVE,
        wpml_imageFormat: o.imageFormat.join()
      }
    }]
  }
  const p = {
    wpml_missionConfig: {
      ...e
    },
    Folder: l
  }
  return {
    templateInfo: n,
    waylinesInfo: p
  }
}

export const generatePolygonWaypoints = (
  polygonRoutes: Array<{ geographicPoints: number[][] }>,
  config: WaypointConfig,
  referencePoints: number[][]
): any[] => {
  const waypointRoutes = []
  const avgReferencePoint = calculateAveragePosition(referencePoints)

  polygonRoutes.forEach((route, routeIndex) => {
    const startActions = createStartActions(config.payloadEnumValue, routeIndex > 0)
    const routeWaypoints = []
    const avgPoint = calculateAveragePosition(route.geographicPoints)

    const centerCartesian = Cesium.Cartesian3.fromDegrees(avgPoint.lng, avgPoint.lat, 0)
    const referenceCartesian = Cesium.Cartesian3.fromDegrees(
      avgReferencePoint.lng,
      avgReferencePoint.lat,
      0
    )

    const headingAngle = Cesium.Math.toDegrees(
      getPointPosition(centerCartesian, referenceCartesian).h
    )

    route.geographicPoints.forEach((point, pointIndex) => {
      const altitude = point[2] || 0
      let waypointHeading = FollowType.FOLLOW_WAYLINE
      let headingAngleForPoint = 0

      if (routeIndex === 0 && pointIndex < route.geographicPoints.length - 1) {
        const currentCartesian = Cesium.Cartesian3.fromDegrees(point[0], point[1], 0)
        const nextCartesian = Cesium.Cartesian3.fromDegrees(
          route.geographicPoints[pointIndex + 1][0],
          route.geographicPoints[pointIndex + 1][1],
          0
        )
        headingAngleForPoint = Cesium.Math.toDegrees(
          getPointPosition(currentCartesian, nextCartesian).h
        )
      } else if (routeIndex !== 0 && pointIndex < route.geographicPoints.length - 1) {
        headingAngleForPoint = headingAngle
      }

      if (routeIndex !== 0 && pointIndex < route.geographicPoints.length - 1) {
        waypointHeading = FollowType.FIXED
      }

      const waypoint = {
        Point: new GetPoint(point[0], point[1]),
        wpml_index: pointIndex,
        wpml_executeHeight: altitude,
        wpml_waypointSpeed: config.autoFlightSpeed,
        wpml_waypointHeadingParam: {
          wpml_waypointHeadingMode: waypointHeading,
          wpml_waypointHeadingAngle: headingAngleForPoint,
          wpml_waypointPoiPoint: new GetNewPoint().toString(),
          wpml_waypointHeadingAngleEnable: 1,
          wpml_waypointHeadingPathMode: FollowBadType.FOLLOW_BAD_ARC,
          wpml_waypointHeadingPoiIndex: 0
        },
        wpml_waypointTurnParam: {
          wpml_waypointTurnMode: WaylineSmoothType.TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE,
          wpml_waypointTurnDampingDist: 0
        },
        wpml_useStraightLine: 1,
        wpml_waypointGimbalHeadingParam: {
          wpml_waypointGimbalPitchAngle: 0,
          wpml_waypointGimbalYawAngle: 0
        },
        wpml_isRisky: BooleanEnum.FALSE,
        wpml_waypointWorkType: BooleanEnum.FALSE
      }

      const actions = generatePayloadActions(
        config.payloadVal,
        config,
        route.geographicPoints.length,
        routeIndex
      )

      if (pointIndex === 0) {
        waypoint.wpml_actionGroup = actions.firstActionGroup
      } else if (pointIndex === route.geographicPoints.length - 1) {
        waypoint.wpml_actionGroup = actions.lastActionGroup
      }

      routeWaypoints.push(waypoint)
    })
    const length = route.geographicPoints.map(el => ({ longitude: el[0] * 1, latitude: el[1] * 1, height: el[2] || 0 }))
    const distance = calculateCesiumPolylineLength(length)
    const duration = distance / config.autoFlightSpeed
    waypointRoutes.push({
      wpml_templateId: 0,
      wpml_executeHeightMode: WaylineTypeMap.WGS84,
      wpml_waylineId: routeIndex,
      wpml_distance: distance,
      wpml_duration: duration,
      wpml_autoFlightSpeed: config.autoFlightSpeed,
      wpml_startActionGroup: startActions,
      Placemark: routeWaypoints
    })
  })

  return waypointRoutes
}

export function createStartActions (payloadType: PAYLOAD_TRY_TYPE, isSecondaryRoute?: boolean): any[] {
  const basePitchAngle = isSecondaryRoute ? -45 : -90

  if ([PAYLOAD_TRY_TYPE.M30D, PAYLOAD_TRY_TYPE.M30T].includes(payloadType)) {
    return []
  }

  if (payloadType === PAYLOAD_TRY_TYPE.M3T) {
    return [{
      wpml_action: [
        {
          wpml_actionId: 0,
          ...createGimbalRotateAction(basePitchAngle)
        },
        {
          wpml_actionId: 1,
          wpml_actionActuatorFunc: WaylineActionType.HOVER,
          wpml_actionActuatorFuncParam: {
            wpml_hoverTime: 1
          }
        }
      ]
    }]
  }

  return [{
    wpml_action: [
      {
        wpml_actionId: 0,
        ...createGimbalRotateAction(basePitchAngle)
      },
      {
        wpml_actionId: 1,
        wpml_actionActuatorFunc: WaylineActionType.HOVER,
        wpml_actionActuatorFuncParam: {
          wpml_hoverTime: 0.5
        }
      },
      {
        wpml_actionId: 2,
        wpml_actionActuatorFunc: WaylineActionType.SET_FOCUS_TYPE,
        wpml_actionActuatorFuncParam: {
          wpml_cameraFocusType: GIMBAL_PITCH_MODE_TYPE.MANUAL,
          wpml_payloadPositionIndex: 0
        }
      },
      {
        wpml_actionId: 3,
        wpml_actionActuatorFunc: WaylineActionType.FOCUS,
        wpml_actionActuatorFuncParam: {
          wpml_focusX: 0,
          wpml_focusY: 0,
          wpml_focusRegionWidth: 0,
          wpml_focusRegionHeight: 0,
          wpml_isPointFocus: 0,
          wpml_isInfiniteFocus: 0,
          wpml_payloadPositionIndex: 0,
          wpml_isCalibrationFocus: 0
        }
      },
      {
        wpml_actionId: 4,
        wpml_actionActuatorFunc: WaylineActionType.HOVER,
        wpml_actionActuatorFuncParam: {
          wpml_hoverTime: 1
        }
      }
    ]
  }]
}

const DEFAULT_SHOOT_INTERVAL = 6
const DEFAULT_ACTION_TRIGGER_PARAM = 7

function generatePayloadActions (
  payloadType: PAYLOAD_TRY_TYPE,
  config: WaypointConfig,
  pointCount: number,
  routeIndex: number
): { firstActionGroup: any[], lastActionGroup: any[] } {
  const triggerType = config.shootType === SHOOT_TYPE.DISTANCE
    ? PointEnum.MULTIPLE_DISTANCE
    : PointEnum.MULTIPLE_TIMING

  const gimbalAction = createGimbalRotateAction(routeIndex === 0 ? -90 : -45)

  const timeLapseActions = {
    start: {
      wpml_actionActuatorFunc: WaylineActionType.START_TIME_LAPSE,
      wpml_actionActuatorFuncParam: {
        wpml_payloadPositionIndex: 0,
        wpml_useGlobalPayloadLensIndex: 0,
        wpml_payloadLensIndex: config.imageFormat.join(),
        wpml_minShootInterval: DEFAULT_SHOOT_INTERVAL
      }
    },
    stop: {
      wpml_actionActuatorFunc: WaylineActionType.STOP_TIME_LAPSE,
      wpml_actionActuatorFuncParam: {
        wpml_payloadPositionIndex: 0,
        wpml_payloadLensIndex: config.imageFormat.join()
      }
    }
  }

  const continuousShootActions = {
    start: {
      wpml_actionActuatorFunc: WaylineActionType.START_CONTINUOUS_SHOOTING,
      wpml_actionActuatorFuncParam: {
        wpml_payloadPositionIndex: 0,
        wpml_useGlobalPayloadLensIndex: 0,
        wpml_payloadLensIndex: config.imageFormat.join()
      }
    },
    stop: {
      wpml_actionActuatorFunc: WaylineActionType.STOP_CONTINUOUS_SHOOTING,
      wpml_actionActuatorFuncParam: {
        wpml_payloadPositionIndex: 0,
        wpml_payloadLensIndex: config.imageFormat.join()
      }
    }
  }

  const baseActions = {
    firstActionGroup: [] as any[],
    lastActionGroup: [] as any[]
  }

  // 处理不同类型的负载
  if ([PAYLOAD_TRY_TYPE.M30D, PAYLOAD_TRY_TYPE.M30T].includes(payloadType)) {
    baseActions.firstActionGroup = [createSimplePhotoActionGroup(
      pointCount,
      triggerType,
      gimbalAction,
      config
    )]
  } else if ([PAYLOAD_TRY_TYPE.M3E, PAYLOAD_TRY_TYPE.M3T].includes(payloadType)) {
    baseActions.lastActionGroup = [createStopActionGroup(pointCount, timeLapseActions.stop)]

    if (config.shootType === SHOOT_TYPE.TIME) {
      baseActions.firstActionGroup = [
        createTimedShootingActionGroup(pointCount, gimbalAction, timeLapseActions.start),
        createTriggeredActionGroup(pointCount, triggerType, gimbalAction)
      ]
    } else {
      baseActions.firstActionGroup = [createContinuousShootingActionGroup(
        pointCount,
        triggerType,
        gimbalAction,
        continuousShootActions.start
      )]
      baseActions.lastActionGroup[0].wpml_action[0] = {
        wpml_actionId: 0,
        ...continuousShootActions.stop
      }
    }
  } else {
    baseActions.lastActionGroup = [{
      wpml_actionGroupId: 2,
      wpml_actionGroupStartIndex: pointCount - 1,
      wpml_actionGroupEndIndex: pointCount - 1,
      wpml_actionGroupMode: SEQUENCE.SEQUENCE,
      wpml_actionTrigger: {
        wpml_actionTriggerType: PointEnum.REACH_POINT
      },
      wpml_action: [
        { wpml_actionId: 0, ...timeLapseActions.stop },
        {
          wpml_actionId: 1,
          wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ANGLE_UNLOCK
        }
      ]
    }]

    if (config.shootType === SHOOT_TYPE.TIME) {
      baseActions.firstActionGroup = [
        createGimbalLockActionGroup(pointCount, gimbalAction, timeLapseActions.start),
        createTriggeredActionGroup(pointCount, triggerType, gimbalAction)
      ]
    } else {
      baseActions.firstActionGroup = [
        createGimbalLockOnlyActionGroup(pointCount),
        createContinuousShootingActionGroup(
          pointCount,
          triggerType,
          gimbalAction,
          continuousShootActions.start
        )
      ]
      baseActions.lastActionGroup[0].wpml_action[0] = {
        wpml_actionId: 0,
        ...continuousShootActions.stop
      }
    }
  }

  return baseActions
}

//  提取重复的动作组创建逻辑
function createSimplePhotoActionGroup (
  pointCount: number,
  triggerType: PointEnum,
  gimbalAction: any,
  config: WaypointConfig
): any {
  return {
    wpml_actionGroupId: 0,
    wpml_actionGroupStartIndex: 0,
    wpml_actionGroupEndIndex: pointCount - 1,
    wpml_actionGroupMode: SEQUENCE.SEQUENCE,
    wpml_actionTrigger: {
      wpml_actionTriggerType: triggerType,
      wpml_actionTriggerParam: DEFAULT_ACTION_TRIGGER_PARAM
    },
    wpml_action: [
      {
        wpml_actionId: 0,
        ...gimbalAction
      },
      {
        wpml_actionId: 1,
        wpml_actionActuatorFunc: WaylineActionType.TAKE_PHOTO,
        wpml_actionActuatorFuncParam: {
          wpml_payloadPositionIndex: 0,
          wpml_useGlobalPayloadLensIndex: 0,
          wpml_payloadLensIndex: config.imageFormat.join()
        }
      }
    ]
  }
}

function createStopActionGroup (pointCount: number, stopAction: any): any {
  return {
    wpml_actionGroupId: 2,
    wpml_actionGroupStartIndex: pointCount - 1,
    wpml_actionGroupEndIndex: pointCount - 1,
    wpml_actionGroupMode: SEQUENCE.SEQUENCE,
    wpml_actionTrigger: {
      wpml_actionTriggerType: PointEnum.REACH_POINT
    },
    wpml_action: [
      { wpml_actionId: 0, ...stopAction }
    ]
  }
}

function createTimedShootingActionGroup (
  pointCount: number,
  gimbalAction: any,
  startTimeLapseAction: any
): any {
  return {
    wpml_actionGroupId: 0,
    wpml_actionGroupStartIndex: 0,
    wpml_actionGroupEndIndex: pointCount - 1,
    wpml_actionGroupMode: SEQUENCE.SEQUENCE,
    wpml_actionTrigger: {
      wpml_actionTriggerType: PointEnum.BETWEEN_ADJACENT_POINTS
    },
    wpml_action: [
      {
        wpml_actionId: 0,
        ...gimbalAction
      },
      {
        wpml_actionId: 1,
        ...startTimeLapseAction
      }
    ]
  }
}

function createTriggeredActionGroup (
  pointCount: number,
  triggerType: PointEnum,
  gimbalAction: any
): any {
  return {
    wpml_actionGroupId: 1,
    wpml_actionGroupStartIndex: 0,
    wpml_actionGroupEndIndex: pointCount - 1,
    wpml_actionGroupMode: SEQUENCE.SEQUENCE,
    wpml_actionTrigger: {
      wpml_actionTriggerType: triggerType,
      wpml_actionTriggerParam: DEFAULT_ACTION_TRIGGER_PARAM
    },
    wpml_action: [
      {
        wpml_actionId: 0,
        ...gimbalAction
      }
    ]
  }
}

function createContinuousShootingActionGroup (
  pointCount: number,
  triggerType: PointEnum,
  gimbalAction: any,
  startContinuousAction: any
): any {
  return {
    wpml_actionGroupId: 1,
    wpml_actionGroupStartIndex: 0,
    wpml_actionGroupEndIndex: pointCount - 1,
    wpml_actionGroupMode: SEQUENCE.SEQUENCE,
    wpml_actionTrigger: {
      wpml_actionTriggerType: triggerType,
      wpml_actionTriggerParam: DEFAULT_ACTION_TRIGGER_PARAM
    },
    wpml_action: [
      {
        wpml_actionId: 0,
        ...gimbalAction
      },
      {
        wpml_actionId: 1,
        ...startContinuousAction
      }
    ]
  }
}

function createGimbalLockActionGroup (
  pointCount: number,
  gimbalAction: any,
  startTimeLapseAction: any
): any {
  return {
    wpml_actionGroupId: 0,
    wpml_actionGroupStartIndex: 0,
    wpml_actionGroupEndIndex: pointCount - 1,
    wpml_actionGroupMode: SEQUENCE.SEQUENCE,
    wpml_actionTrigger: {
      wpml_actionTriggerType: PointEnum.BETWEEN_ADJACENT_POINTS
    },
    wpml_action: [
      {
        wpml_actionId: 0,
        wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ANGLE_LOCK
      },
      {
        wpml_actionId: 1,
        ...gimbalAction
      },
      {
        wpml_actionId: 2,
        ...startTimeLapseAction
      }
    ]
  }
}

function createGimbalLockOnlyActionGroup (pointCount: number): any {
  return {
    wpml_actionGroupId: 0,
    wpml_actionGroupStartIndex: 0,
    wpml_actionGroupEndIndex: pointCount - 1,
    wpml_actionGroupMode: SEQUENCE.SEQUENCE,
    wpml_actionTrigger: {
      wpml_actionTriggerType: PointEnum.BETWEEN_ADJACENT_POINTS
    },
    wpml_action: [
      {
        wpml_actionId: 0,
        wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ANGLE_LOCK
      }
    ]
  }
}

/**
 * 根据负载类型和拍摄方式生成首、尾动作组
 * @param {string} payloadType        - 负载类型（如 PAYLOAD_TRY_TYPE.M30D）
 * @param {object} config             - 配置对象 { shootType, imageFormat }
 * @param {number} totalPoints        - 总航点数量
 * @param {number} isFirstPoint       - 是否为第一个点（0 表示第一个点，非0表示其他）
 * @returns {{ firstActionGroup: Array, lastActionGroup: Array }}
 */
export function getPointWpml (payloadType, config, totalPoints, isFirstPoint) {
  const { shootType, imageFormat } = config
  const payloadLensIndex = imageFormat ? imageFormat.join(',') : '' // 与原行为一致（默认逗号分隔）

  // 拍摄触发类型
  const triggerType = shootType === SHOOT_TYPE.DISTANCE
    ? PointEnum.MULTIPLE_DISTANCE
    : PointEnum.MULTIPLE_TIMING

  // ---------- 预定义可复用的动作对象（避免重复定义）----------
  const gimbalRotateAction = {
    wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ROTATE,
    wpml_actionActuatorFuncParam: {
      wpml_gimbalHeadingYawBase: GIMBAL_HEADERING_TYPE.AIRCRAFT,
      wpml_gimbalRotateMode: GIMBAL_ROTATE_MODE_TYPE.ABSOLUTE_ANGLE,
      wpml_gimbalPitchRotateEnable: 1,
      wpml_gimbalPitchRotateAngle: isFirstPoint === 0 ? -90 : -45,
      wpml_gimbalRollRotateEnable: 0,
      wpml_gimbalRollRotateAngle: 0,
      wpml_gimbalYawRotateEnable: 1,
      wpml_gimbalYawRotateAngle: 0,
      wpml_gimbalRotateTimeEnable: 0,
      wpml_gimbalRotateTime: 10,
      wpml_payloadPositionIndex: 0,
    },
  }

  const startTimeLapseAction = {
    wpml_actionActuatorFunc: WaylineActionType.START_TIME_LAPSE,
    wpml_actionActuatorFuncParam: {
      wpml_payloadPositionIndex: 0,
      wpml_useGlobalPayloadLensIndex: 0,
      wpml_payloadLensIndex: payloadLensIndex,
      wpml_minShootInterval: 6,
    },
  }

  const stopTimeLapseAction = {
    wpml_actionActuatorFunc: WaylineActionType.STOP_TIME_LAPSE,
    wpml_actionActuatorFuncParam: {
      wpml_payloadPositionIndex: 0,
      wpml_payloadLensIndex: payloadLensIndex,
    },
  }

  const startContinuousAction = {
    wpml_actionActuatorFunc: WaylineActionType.START_CONTINUOUS_SHOOTING,
    wpml_actionActuatorFuncParam: {
      wpml_payloadPositionIndex: 0,
      wpml_useGlobalPayloadLensIndex: 0,
      wpml_payloadLensIndex: payloadLensIndex,
    },
  }

  const stopContinuousAction = {
    wpml_actionActuatorFunc: WaylineActionType.STOP_CONTINUOUS_SHOOTING,
    wpml_actionActuatorFuncParam: {
      wpml_payloadPositionIndex: 0,
      wpml_payloadLensIndex: payloadLensIndex,
    },
  }

  const takePhotoAction = {
    wpml_actionId: 1,
    wpml_actionActuatorFunc: WaylineActionType.TAKE_PHOTO,
    wpml_actionActuatorFuncParam: {
      wpml_payloadPositionIndex: 0,
      wpml_useGlobalPayloadLensIndex: 0,
      wpml_payloadLensIndex: payloadLensIndex,
    },
  }

  // 根据负载类型构建首、尾动作组
  let firstActionGroup = []
  let lastActionGroup = []

  if (payloadType === PAYLOAD_TRY_TYPE.M30D || payloadType === PAYLOAD_TRY_TYPE.M30T) {
    // M30D / M30T
    firstActionGroup = [{
      wpml_actionGroupId: 0,
      wpml_actionGroupStartIndex: 0,
      wpml_actionGroupEndIndex: totalPoints - 1,
      wpml_actionGroupMode: SEQUENCE.SEQUENCE,
      wpml_actionTrigger: {
        wpml_actionTriggerType: triggerType,
        wpml_actionTriggerParam: 7,
      },
      wpml_action: [
        { wpml_actionId: 0, ...gimbalRotateAction },
        takePhotoAction,
      ],
    }]
    lastActionGroup = []
  } else if (payloadType === PAYLOAD_TRY_TYPE.M3E || payloadType === PAYLOAD_TRY_TYPE.M3T) {
    // M3E / M3T
    lastActionGroup = [{
      wpml_actionGroupId: 2,
      wpml_actionGroupStartIndex: totalPoints - 1,
      wpml_actionGroupEndIndex: totalPoints - 1,
      wpml_actionGroupMode: SEQUENCE.SEQUENCE,
      wpml_actionTrigger: {
        wpml_actionTriggerType: PointEnum.REACH_POINT,
      },
      wpml_action: [{ wpml_actionId: 0, ...stopTimeLapseAction }],
    }]

    if (shootType === SHOOT_TYPE.TIME) {
      firstActionGroup = [
        {
          wpml_actionGroupId: 0,
          wpml_actionGroupStartIndex: 0,
          wpml_actionGroupEndIndex: totalPoints - 1,
          wpml_actionGroupMode: SEQUENCE.SEQUENCE,
          wpml_actionTrigger: {
            wpml_actionTriggerType: PointEnum.BETWEEN_ADJACENT_POINTS,
          },
          wpml_action: [
            { wpml_actionId: 0, ...gimbalRotateAction },
            { wpml_actionId: 1, ...startTimeLapseAction },
          ],
        },
        {
          wpml_actionGroupId: 1,
          wpml_actionGroupStartIndex: 0,
          wpml_actionGroupEndIndex: totalPoints - 1,
          wpml_actionGroupMode: SEQUENCE.SEQUENCE,
          wpml_actionTrigger: {
            wpml_actionTriggerType: triggerType,
            wpml_actionTriggerParam: 7,
          },
          wpml_action: [{ wpml_actionId: 0, ...gimbalRotateAction }],
        },
      ]
    } else {
      firstActionGroup = [{
        wpml_actionGroupId: 1,
        wpml_actionGroupStartIndex: 0,
        wpml_actionGroupEndIndex: totalPoints - 1,
        wpml_actionGroupMode: SEQUENCE.SEQUENCE,
        wpml_actionTrigger: {
          wpml_actionTriggerType: triggerType,
          wpml_actionTriggerParam: 7,
        },
        wpml_action: [
          { wpml_actionId: 0, ...gimbalRotateAction },
          { wpml_actionId: 1, ...startContinuousAction },
        ],
      }]
      // 修改尾动作组的第一个动作为停止连续拍摄
      lastActionGroup[0].wpml_action[0] = { wpml_actionId: 0, ...stopContinuousAction }
    }
  } else {
    // 其他负载类型（默认分支）
    lastActionGroup = [{
      wpml_actionGroupId: 2,
      wpml_actionGroupStartIndex: totalPoints - 1,
      wpml_actionGroupEndIndex: totalPoints - 1,
      wpml_actionGroupMode: SEQUENCE.SEQUENCE,
      wpml_actionTrigger: {
        wpml_actionTriggerType: PointEnum.REACH_POINT,
      },
      wpml_action: [
        { wpml_actionId: 0, ...stopTimeLapseAction },
        { wpml_actionId: 1, wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ANGLE_UNLOCK },
      ],
    }]

    if (shootType === SHOOT_TYPE.TIME) {
      firstActionGroup = [
        {
          wpml_actionGroupId: 0,
          wpml_actionGroupStartIndex: 0,
          wpml_actionGroupEndIndex: totalPoints - 1,
          wpml_actionGroupMode: SEQUENCE.SEQUENCE,
          wpml_actionTrigger: {
            wpml_actionTriggerType: PointEnum.BETWEEN_ADJACENT_POINTS,
          },
          wpml_action: [
            { wpml_actionId: 0, wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ANGLE_LOCK },
            { wpml_actionId: 1, ...gimbalRotateAction },
            { wpml_actionId: 2, ...startTimeLapseAction },
          ],
        },
        {
          wpml_actionGroupId: 1,
          wpml_actionGroupStartIndex: 0,
          wpml_actionGroupEndIndex: totalPoints - 1,
          wpml_actionGroupMode: SEQUENCE.SEQUENCE,
          wpml_actionTrigger: {
            wpml_actionTriggerType: triggerType,
            wpml_actionTriggerParam: 7,
          },
          wpml_action: [{ wpml_actionId: 0, ...gimbalRotateAction }],
        },
      ]
    } else {
      firstActionGroup = [
        {
          wpml_actionGroupId: 0,
          wpml_actionGroupStartIndex: 0,
          wpml_actionGroupEndIndex: totalPoints - 1,
          wpml_actionGroupMode: SEQUENCE.SEQUENCE,
          wpml_actionTrigger: {
            wpml_actionTriggerType: PointEnum.BETWEEN_ADJACENT_POINTS,
          },
          wpml_action: [
            { wpml_actionId: 0, wpml_actionActuatorFunc: WaylineActionType.GIMBAL_ANGLE_LOCK },
          ],
        },
        {
          wpml_actionGroupId: 1,
          wpml_actionGroupStartIndex: 0,
          wpml_actionGroupEndIndex: totalPoints - 1,
          wpml_actionGroupMode: SEQUENCE.SEQUENCE,
          wpml_actionTrigger: {
            wpml_actionTriggerType: triggerType,
            wpml_actionTriggerParam: 7,
          },
          wpml_action: [
            { wpml_actionId: 0, ...gimbalRotateAction },
            { wpml_actionId: 1, ...startContinuousAction },
          ],
        },
      ]
      // 替换尾动作组的第一个动作为停止连续拍摄
      lastActionGroup[0].wpml_action[0] = { wpml_actionId: 0, ...stopContinuousAction }
    }
  }

  return { firstActionGroup, lastActionGroup }
}

export const handleSetPoly = generatePolygonWaypoints
export const setCurrentData = updateCurrentRouteData
export const changePayloadsNumber = calculateFocalLengthForPayload
export const changePaypladNumber = calculateFocalLengthForPayloads
